import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";

export default function CameraCapture({ open, onClose, onCapture, title = "Chụp ảnh CCCD" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [captured, setCaptured] = useState(null);
  const [facing, setFacing] = useState("environment");

  useEffect(() => {
    if (!open) return;
    let active = true;
    setCaptured(null);
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing },
          audio: false,
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (e) {
        toast.error("Không thể mở camera. Vui lòng cấp quyền hoặc dùng tải ảnh lên.");
        onClose();
      }
    })();
    return () => {
      active = false;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [open, facing]);

  const snap = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        const file = new File([blob], `cccd-${Date.now()}.jpg`, { type: "image/jpeg" });
        setCaptured({ url: URL.createObjectURL(blob), file });
      },
      "image/jpeg",
      0.9
    );
  };

  const confirm = () => {
    onCapture(captured.file);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-800 max-w-lg p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b border-zinc-800">
          <DialogTitle className="font-display text-white flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" /> {title}
          </DialogTitle>
        </DialogHeader>

        <div className="relative w-full aspect-[8/5] bg-black overflow-hidden flex items-center justify-center">
          {captured ? (
            <img src={captured.url} alt="captured" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
          )}
          {/* dark border overlay */}
          <div className="absolute inset-0 z-10 box-border border-[36px] sm:border-[56px] border-black/60 pointer-events-none" />
          {/* guide frame with 4 red corners */}
          <div className="absolute inset-0 z-20 m-[36px] sm:m-[56px] pointer-events-none">
            <div className="absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 border-primary" />
            <div className="absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 border-primary" />
            <div className="absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 border-primary" />
            <div className="absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 border-primary" />
          </div>
          <div className="absolute bottom-3 left-0 right-0 text-center text-white font-semibold text-xs z-30 drop-shadow">
            Căn 4 góc thẻ vào khung đỏ
          </div>
        </div>

        <div className="p-4 flex items-center justify-center gap-3 border-t border-zinc-800">
          {captured ? (
            <>
              <button
                data-testid="camera-retake"
                onClick={() => setCaptured(null)}
                className="flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:text-white rounded-sm px-5 py-2 text-sm transition-colors"
              >
                <RefreshCw className="w-4 h-4" /> Chụp lại
              </button>
              <button
                data-testid="camera-confirm"
                onClick={confirm}
                className="flex items-center gap-2 bg-primary hover:bg-[#ff1a1a] text-white font-bold rounded-sm px-6 py-2 text-sm transition-colors"
              >
                <Check className="w-4 h-4" /> Dùng ảnh này
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="camera-flip"
                onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}
                className="flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:text-white rounded-sm px-4 py-2 text-sm transition-colors"
              >
                <RefreshCw className="w-4 h-4" /> Đổi camera
              </button>
              <button
                data-testid="camera-snap"
                onClick={snap}
                className="flex items-center gap-2 bg-primary hover:bg-[#ff1a1a] text-white font-bold rounded-sm px-6 py-2 text-sm transition-colors shadow-[0_0_15px_rgba(230,0,0,0.35)]"
              >
                <Camera className="w-4 h-4" /> Chụp
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
